import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api, tokens, PASSWORD_CHANGE_EVENT, SIGNED_OUT_EVENT } from './lib/api.js';
import { useAppVersion } from './lib/version.js';
import { useKeyboardShortcut } from './components/charts.jsx';
import { Alert, ErrorBoundary, ThemeToggle, initTheme } from './components/ui.jsx';
import { ROLES, can } from './lib/roles.js';
import Platform from './pages/Platform.jsx';
import ChangePassword from './components/ChangePassword.jsx';
import Login from './pages/Login.jsx';
import Dashboard from './pages/Dashboard.jsx';
import DeliveryOrders from './pages/DeliveryOrders.jsx';
import NewDeliveryOrder from './pages/NewDeliveryOrder.jsx';
import GodownQueue from './pages/GodownQueue.jsx';
import Deliveries from './pages/Deliveries.jsx';
import Invoices from './pages/Invoices.jsx';
import CreditNotes from './pages/CreditNotes.jsx';
import Approvals from './pages/Approvals.jsx';
import PriceLists from './pages/PriceLists.jsx';
import Customers from './pages/Customers.jsx';
import Reconciliation from './pages/Reconciliation.jsx';
import Returns from './pages/Returns.jsx';
import Settings from './pages/Settings.jsx';
import Transfers from './pages/Transfers.jsx';
import Analytics from './pages/Analytics.jsx';
import CreditNoteDetail from './pages/CreditNoteDetail.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import ForcePasswordChange from './pages/ForcePasswordChange.jsx';
import InvoiceDetail from './pages/InvoiceDetail.jsx';
import Batches from './pages/Batches.jsx';
import TestCertificates from './pages/TestCertificates.jsx';
import SyncAdmin from './pages/SyncAdmin.jsx';
import LedgerShieldPage from './pages/LedgerShieldPage.jsx';
import Receivables from './pages/Receivables.jsx';
import SalesRegister from './pages/SalesRegister.jsx';

const SessionContext = createContext(null);
export const useSession = () => useContext(SessionContext);

// The company being worked on, remembered per browser. Screens read
// session.entities[0], so the chosen one is put first rather than every screen
// learning about a selection.
const ENTITY_KEY = 'invit.entity';
const readEntity = () => { try { return localStorage.getItem(ENTITY_KEY); } catch { return null; } };
const writeEntity = (id) => { try { localStorage.setItem(ENTITY_KEY, id); } catch { /* storage blocked */ } };
const withCurrentFirst = (entities, id) => {
  const list = entities || [];
  const current = list.find((e) => e.id === id);
  return current ? [current, ...list.filter((e) => e !== current)] : list;
};

const NAV = [
  { to: '/', label: 'Dashboard', end: true, group: 'Overview' },
  { to: '/delivery-orders', label: 'Delivery orders', group: 'Floor' },
  { to: '/godown', label: 'Godown queue', group: 'Floor', roles: ['tenant_admin', 'godown_operator'] },
  { to: '/deliveries', label: 'Despatches', group: 'Floor' },
  { to: '/transfers', label: 'Stock transfers', group: 'Floor', roles: ['tenant_admin', 'godown_operator', 'accounts'] },
  { to: '/invoices', label: 'Tax invoices', group: 'Billing' },
  { to: '/credit-notes', label: 'Credit notes', group: 'Billing' },
  { to: '/batches', label: 'Batches & specs', group: 'Billing' },
  { to: '/test-certificates', label: 'Test certificates', group: 'Billing' },
  { to: '/sync', label: 'Tally sync', group: 'Billing', roles: ['tenant_admin', 'accounts'] },
  { to: '/reconciliation', label: 'Reconciliation', group: 'Billing', roles: ['tenant_admin', 'accounts'] },
  { to: '/returns', label: 'GST returns', group: 'Billing', roles: ['tenant_admin', 'accounts', 'sales_counter'] },
  { to: '/analytics', label: 'Margin & ageing', group: 'Billing', roles: ['tenant_admin', 'accounts'] },
  { to: '/sales-register', label: 'Sales register', group: 'Reports', roles: ['tenant_admin', 'accounts', 'sales_counter'] },
  { to: '/receivables', label: 'Receivables', group: 'Reports', roles: ['tenant_admin', 'accounts', 'sales_counter'] },
  { to: '/tally', label: 'Tally dashboard', group: 'Books', end: true, roles: ['tenant_admin', 'accounts'], feature: 'tally_dashboard' },
  { to: '/tally/reports', label: 'Tally reports', group: 'Books', roles: ['tenant_admin', 'accounts'], feature: 'tally_reports' },
  { to: '/approvals', label: 'Approvals', group: 'Control', roles: ROLES.COMMERCIAL },
  { to: '/price-lists', label: 'Price lists', group: 'Control', roles: ['tenant_admin', 'accounts', 'sales_counter'] },
  { to: '/customers', label: 'Customers', group: 'Control', roles: ['tenant_admin', 'accounts', 'sales_counter'] },
  { to: '/settings', label: 'Settings', group: 'Control', roles: ['tenant_admin', 'accounts'] },
];

/**
 * The temporary-password flag can come from /me, from the login response, or
 * from the access token itself; any one of them is enough to hold the screen.
 */
const withFlags = (me) => (me && me.user
  ? { ...me, user: { ...me.user, must_change_password: Boolean(me.user.must_change_password || tokens.mustChangePassword()) } }
  : me);

export default function App() {
  const [session, setRawSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState(null);
  const [entityId, setEntityId] = useState(readEntity);
  const setSession = (me) => setRawSession(withFlags(me));
  const chooseEntity = (id) => { writeEntity(id); setEntityId(id); };

  useEffect(initTheme, []);

  // Only a refused session signs the person out. A network blip or a server
  // error while starting up must not throw away tokens that are still good.
  const loadMe = () => {
    if (!tokens.get()) { setLoading(false); return; }
    setLoading(true); setBootError(null);
    api.get('/api/auth/me').then(setSession).catch((e) => {
      if (e && e.status === 401) tokens.clear();
      else setBootError(e && e.message ? e.message : 'Could not reach InvIT');
    }).finally(() => setLoading(false));
  };
  useEffect(loadMe, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The API layer announces a session that could not be refreshed, and a call
  // refused because the password is still the temporary one.
  useEffect(() => {
    const onSignedOut = () => setRawSession(null);
    const onMustChange = () => setRawSession((s) => (s ? { ...s, user: { ...s.user, must_change_password: true } } : s));
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut);
    window.addEventListener(PASSWORD_CHANGE_EVENT, onMustChange);
    return () => {
      window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut);
      window.removeEventListener(PASSWORD_CHANGE_EVENT, onMustChange);
    };
  }, []);

  if (loading) {
    return <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }} className="muted">Loading InvIT…</div>;
  }

  // A reset link is the one screen that works without a session.
  if (window.location.pathname === '/reset-password') return <ResetPassword />;

  if (bootError && !session) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 16 }}>
        <div style={{ maxWidth: 420, width: '100%' }}>
          <Alert onRetry={loadMe}>Could not load your session: {bootError}</Alert>
          <button className="btn ghost" style={{ marginTop: 12 }}
            onClick={() => { tokens.clear(); setBootError(null); }}>Sign in again instead</button>
        </div>
      </div>
    );
  }

  if (!session) return <Login onSignedIn={setSession} />;

  // A temporary password gets exactly one screen until it is replaced.
  if (session.user.must_change_password) {
    return <ForcePasswordChange onDone={() => api.get('/api/auth/me').then(setSession)} />;
  }

  const signOut = () => { api.logout(); setRawSession(null); };

  return (
    <SessionContext.Provider value={{
      ...session,
      entities: withCurrentFirst(session.entities, entityId),
      allEntities: session.entities || [],
      chooseEntity,
      refresh: () => api.get('/api/auth/me').then(setSession),
    }}>
      {/* The platform account belongs to no workspace, so the tenant shell has
          nothing it may read. It gets the console for the job it actually does. */}
      {session.user.role === 'super_admin'
        ? <Platform onSignOut={signOut} />
        : <Shell onSignOut={signOut} />}
    </SessionContext.Provider>
  );
}

function Shell({ onSignOut }) {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [railOpen, setRailOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [alerts, setAlerts] = useState({ tally_failed: 0, spec_blocked: 0, awaiting_invoice: 0 });
  const version = useAppVersion();

  const role = session.user.role;
  const features = (session.subscription && session.subscription.features) || [];
  const nav = NAV.filter((n) => (!n.roles || n.roles.includes(role)) && (!n.feature || features.includes(n.feature)));
  const daysLeft = session.subscription ? session.subscription.days_left : null;
  const renewalDue = daysLeft !== null && daysLeft <= 15 && ['tenant_admin', 'accounts'].includes(role);

  useEffect(() => { setRailOpen(false); }, [location.pathname]);

  // The rail carries live counts, so the thing needing attention is visible
  // from every screen rather than only on the dashboard.
  const entity = session.entities[0] || null;
  const entityId = entity ? entity.id : '';
  useEffect(() => {
    let live = true;
    const load = () => api.get(`/api/reports/analytics?days=7${entityId ? `&entity_id=${entityId}` : ''}`)
      .then((d) => live && setAlerts(d.pipeline))
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { live = false; clearInterval(t); };
  }, [entityId]);

  useKeyboardShortcut({ key: 'k', meta: true }, () => setPaletteOpen(true));

  const badgeFor = (to) => {
    if (to === '/sync' && alerts.tally_failed > 0) return { text: alerts.tally_failed, alert: true };
    if (to === '/batches' && alerts.spec_blocked > 0) return { text: alerts.spec_blocked, alert: true };
    if (to === '/deliveries' && alerts.awaiting_invoice > 0) return { text: alerts.awaiting_invoice };
    if (to === '/godown' && alerts.open_orders > 0) return { text: alerts.open_orders };
    if (to === '/approvals' && alerts.approvals_pending > 0) return { text: alerts.approvals_pending, alert: true };
    return null;
  };

  const groups = [...new Set(nav.map((n) => n.group))];
  const current = nav.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));

  return (
    <div className="shell">
      {railOpen && <div className="rail-scrim only-mobile" onClick={() => setRailOpen(false)} />}
      <nav className={`rail ${railOpen ? 'open' : ''}`} aria-label="Main">
        <div className="rail-brand">
          <div className="rail-mark">IV</div>
          <div>
            <div className="name">InvIT</div>
            <div className="org">{session.user.tenant_name}</div>
          </div>
        </div>

        {groups.map((group) => (
          <div key={group}>
            <div className="rail-group">{group}</div>
            {nav.filter((n) => n.group === group).map((n) => {
              const badge = badgeFor(n.to);
              return (
                <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `nav ${isActive ? 'active' : ''}`}>
                  {n.label}
                  {badge && <span className={`badge ${badge.alert ? 'alert' : ''}`}>{badge.text}</span>}
                </NavLink>
              );
            })}
          </div>
        ))}

        <div className="grow" />
        {version && <div className="rail-version" title="Build version">InvIT v{version}</div>}
        <div className="rail-foot">
          <div className="rail-avatar">{initials(session.user.full_name)}</div>
          <div className="who">
            {session.user.full_name}
            <span>{role.replace(/_/g, ' ')}</span>
          </div>
          <button className="icon-btn" style={{ marginLeft: 'auto' }} title="Change password"
            aria-label="Change password" onClick={() => setPasswordOpen(true)}>🔑</button>
          <button className="icon-btn" title="Sign out" aria-label="Sign out"
            onClick={() => { onSignOut(); navigate('/'); }}>⎋</button>
        </div>
      </nav>

      <div className="content">
        <header className="topbar">
          <button className="icon-btn only-mobile" aria-label="Menu" onClick={() => setRailOpen(true)}>☰</button>
          <div className="crumb">InvIT / <b>{current ? current.label : 'Dashboard'}</b></div>
          <div className="grow" />
          {session.allEntities.length > 1 && (
            <select className="entity-switch" aria-label="Company" value={entityId}
              onChange={(e) => session.chooseEntity(e.target.value)}>
              {session.allEntities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          )}
          <button className="search-hint" aria-label="Jump to…" onClick={() => setPaletteOpen(true)}>
            <span aria-hidden="true">⌕</span> <span className="hint-text">Jump to…</span> <kbd>⌘K</kbd>
          </button>
          <ThemeToggle />
        </header>

        <main className="page">
          {renewalDue && (
            <Alert kind="warn">
              {daysLeft === 0
                ? 'Your InvIT subscription ends today.'
                : `Your InvIT subscription ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.`}
              {' '}Sign-in and the Tally sync stop after that. Contact Authentic Technologies to renew.
            </Alert>
          )}
          <ErrorBoundary key={`${location.pathname}|${entityId}`}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/delivery-orders" element={<DeliveryOrders />} />
            <Route path="/delivery-orders/new" element={<NewDeliveryOrder />} />
            <Route path="/godown" element={<GodownQueue />} />
            <Route path="/deliveries" element={<Deliveries />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/invoices/:id" element={<InvoiceDetail />} />
          <Route path="/credit-notes" element={<CreditNotes />} />
          <Route path="/credit-notes/:id" element={<CreditNoteDetail />} />
          <Route path="/approvals" element={<Approvals />} />
          <Route path="/price-lists" element={<PriceLists />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/reconciliation" element={<Reconciliation />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/transfers" element={<Transfers />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/sales-register" element={<SalesRegister />} />
          <Route path="/receivables" element={<Receivables />} />
            <Route path="/batches" element={<Batches />} />
            <Route path="/test-certificates" element={<TestCertificates />} />
            <Route path="/sync" element={<SyncAdmin />} />
            {features.includes('tally_dashboard') && <Route path="/tally" element={<LedgerShieldPage key="dashboard" page="dashboard" />} />}
            {features.includes('tally_reports') && <Route path="/tally/reports" element={<LedgerShieldPage key="reports" page="reports" />} />}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ErrorBoundary>
        </main>
      </div>

      {paletteOpen && <CommandPalette nav={nav} role={role} onClose={() => setPaletteOpen(false)} />}
      {passwordOpen && <ChangePassword onClose={() => setPasswordOpen(false)} />}
    </div>
  );
}

/** ⌘K / Ctrl-K: go anywhere, or start the one action that matters, without the mouse. */
function CommandPalette({ nav, role, onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);

  const commands = useMemo(() => {
    const actions = [];
    if (can(role, ROLES.COUNTER)) {
      actions.push({ label: 'New delivery order', to: '/delivery-orders/new', hint: 'Action' });
    }
    actions.push({ label: 'Batches waiting for specifications', to: '/batches?spec_status=pending', hint: 'Filter' });
    actions.push({ label: 'Credit notes', to: '/credit-notes', hint: 'Go to' });
    actions.push({ label: 'Approvals waiting', to: '/approvals', hint: 'Filter' });
    actions.push({ label: 'Reconcile with Tally', to: '/reconciliation', hint: 'Go to' });
    actions.push({ label: 'GSTR-1 for this month', to: '/returns', hint: 'Report' });
    actions.push({ label: 'Settings', to: '/settings', hint: 'Go to' });
    actions.push({ label: 'Move stock between godowns', to: '/transfers', hint: 'Action' });
    actions.push({ label: 'Margin this month', to: '/analytics', hint: 'Report' });
    actions.push({ label: 'Sales register', to: '/sales-register', hint: 'Report' });
    actions.push({ label: 'Customers to chase', to: '/receivables?only=overdue', hint: 'Report' });
    actions.push({ label: 'Invoices not yet in Tally', to: '/invoices?sync_status=pending,queued,failed', hint: 'Filter' });
    actions.push({ label: 'Despatches awaiting an invoice', to: '/deliveries?status=confirmed', hint: 'Filter' });
    // An action is offered only where its screen is in this role's nav, so the
    // palette never leads somewhere the rail hides.
    const reachable = (to) => {
      const path = to.split('?')[0];
      const entry = NAV.find((n) => n.to === path);
      return !entry || nav.includes(entry);
    };
    const seen = new Set();
    return [...actions.filter((a) => reachable(a.to)), ...nav.map((n) => ({ label: n.label, to: n.to, hint: 'Go to' }))]
      .filter((c) => (seen.has(c.to) ? false : (seen.add(c.to), true)));
  }, [nav, role]);

  const results = commands.filter((c) => c.label.toLowerCase().includes(query.toLowerCase()));

  const run = (cmd) => { if (cmd) { navigate(cmd.to); onClose(); } };

  return (
    <div className="scrim" onClick={onClose}>
      <div className="palette" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Command palette">
        <input autoFocus value={query} placeholder="Search screens and actions…"
          onChange={(e) => { setQuery(e.target.value); setIndex(0); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowDown') { e.preventDefault(); setIndex((i) => Math.min(results.length - 1, i + 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setIndex((i) => Math.max(0, i - 1)); }
            if (e.key === 'Enter') run(results[index]);
          }} />
        <ul>
          {results.map((c, i) => (
            <li key={c.to} aria-selected={i === index} onMouseEnter={() => setIndex(i)} onClick={() => run(c)}>
              {c.label}<span className="hint">{c.hint}</span>
            </li>
          ))}
          {results.length === 0 && <li className="muted">Nothing matches “{query}”.</li>}
        </ul>
        <div className="pal-foot"><span>↑↓ to move</span><span>↵ to open</span><span>esc to close</span></div>
      </div>
    </div>
  );
}

const initials = (name) => String(name || '?').split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
